<template>
  <div
    v-if="$can('set-income-costs', 'YachtModel')"
    style="height: 100%"
  >
    <v-container
      class="py-0"
      style="height: 100%"
      fluid
    >
      <v-row
        no-gutters
        class="mb-4"
        align="center"
      >
        <v-col cols="auto">
          <div class="headline">
            Yacht Financials
          </div>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            large
            class="mr-4"
            :loading="sheetsSyncing"
            @click="syncSheets()"
          >
            <v-icon
              left
              small
            >
              fa fa-sync
            </v-icon>
            Sync from Sheets
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-autocomplete
            v-model="filters.yachtManufacturerId"
            solo
            label="Yacht Manufacturer"
            :items="yachtManufacturers"
            hide-details
            item-value="id"
            item-text="name"
          />
        </v-col>
      </v-row>

      <v-card class="pa-0">
        <div
          v-if="yachtModels.loading"
          class="pa-6 text-center"
        >
          <v-progress-circular
            :size="50"
            :width="3"
            indeterminate
          />
        </div>
        <v-simple-table
          v-else
          fixed-header
          height="calc(100vh - 140px)"
        >
          <thead>
            <tr>
              <th style="width: 300px; min-width: 300px; max-width: 300px;">
                Yacht Model
              </th>
              <th
                v-for="country in countries"
                :key="country.id"
              >
                {{ country.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="yachtModel in yachtModels.data"
              :key="yachtModel.id"
            >
              <td
                class="text-truncate"
                style="width: 300px; min-width: 300px; max-width: 300px;"
              >
                <div>{{ yachtModel.full_label }}</div>
                <div class="grey--text text--lighten-1 mt-1 caption">
                  #{{ yachtModel.id }} &middot;
                  <strong>{{ yachtModel.model_year }}</strong> &middot;
                  <strong class="grey--text">{{ yachtModel.cabin_count_description }}</strong> cabins &middot;
                  <strong class="grey--text">{{ yachtModel.berth_count_description }}</strong> RNOG
                </div>
              </td>
              <td
                v-for="country in countries"
                :key="country.id"
                class="pa-0"
              >
                <yacht-model-country-table-column
                  :pivot-data="yachtModel.countries.find(c => c.id === country.id) ? yachtModel.countries.find(c => c.id === country.id).pivotData : null"
                  :pivot-data-dirty="yachtModel.countries.find(c => c.id === country.id) ? yachtModel.countries.find(c => c.id === country.id).pivotDataDirty : null"
                  @change="(isDirty, pivotData) => updateYachtModelCountryPivotDirty(yachtModel.id, country.id, isDirty, pivotData)"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </v-container>
    <v-slide-y-reverse-transition>
      <v-row
        v-show="changed.length"
        no-gutters
        justify="center"
        style="position: fixed; bottom:0; left: 0; right: 0; z-index: 2"
      >
        <v-col
          cols="16"
          sm="6"
        >
          <v-card
            class="elevation-24"
            dark
          >
            <v-card-text>
              <v-row
                no-gutters
                align="center"
              >
                <v-col class="px-12">
                  There are unsaved changes.
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    text
                    large
                    color="white"
                    @click="discardChanges"
                  >
                    <v-icon
                      small
                      left
                    >
                      close
                    </v-icon>
                    Discard
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    large
                    color="primary"
                    @click="submit"
                  >
                    <v-icon
                      small
                      left
                    >
                      check
                    </v-icon>
                    Save
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-slide-y-reverse-transition>
  </div>
</template>

<script>
import YachtModel from '@/models/YachtModel'
import YachtModelCountryTableColumn from '@/components/settings/yachtFinancials/YachtModelCountryTableColumn'

export default {
    name: 'YachtFinancialsIndex',
    components: {
      YachtModelCountryTableColumn
    },
    data() {
      return {
        yachtModels: {
          data: [],
          loading: false
        },
        filters: {
          yachtManufacturerId: undefined
        },
        submitting: false,
        changed: [],
        sheetsSyncing: false
      }
    },
    computed: {
      countries() {
        return this.$store.state.settings.countries.data.filter(c => c.chartering)
      },
      yachtManufacturers() {
        return this.$store.state.settings.yachtManufacturers.data
      }
    },
    watch: {
      yachtManufacturers(newValue) {
        if (!newValue) return

        this.filters.yachtManufacturerId = this.yachtManufacturers[0].id
      },
      filters: {
        deep: true,
        handler: function () {
          this.fetchYachtModels()
        }
      }
    },
    created() {
      if (this.yachtManufacturers.length > 0) {
        this.filters.yachtManufacturerId = this.yachtManufacturers[0].id
      }

      this.fetchYachtModels()
    },
    methods: {
      async fetchYachtModels() {
        if (!this.filters.yachtManufacturerId) return

        this.yachtModels.loading = true

        try {
          let response = await YachtModel.api.fetch({
            status: YachtModel.STATUS_ACTIVE,
            yacht_manufacturer_id: this.filters.yachtManufacturerId,
            is_sales_active: true,
            per_page: 50
          })

          this.yachtModels.data = this.transformYachtModelsArray(response.data)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtModels.loading = false
        }
      },
      async submit () {
        if (!this.changed.length) return

        this.submitting = true

        const data = this.changed.map(item => {
          let yachtModel = this.yachtModels.data.find(ym => ym.id === item.yacht_model_id)
          let country = yachtModel.countries.find(c => c.id === item.country_id)

          return {
            yacht_model_id: item.yacht_model_id,
            country_id: item.country_id,
            data: country.pivotDataDirty
          }
        })

        try {
          let updatedPivots = await YachtModel.api.updateCountryPivots({ data: data })

          this.updateYachtModelCountryPivots(updatedPivots)
          this.changed = []

          this.$store.dispatch('alert', {
            'text': 'Yacht model country prices saved',
            'color': 'success'
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      updateYachtModelCountryPivotDirty(yachtModelId, countryId, isDirty, pivotData) {
        let yachtModel = this.yachtModels.data.find(ym => ym.id === yachtModelId)
        let country = yachtModel.countries.find(c => c.id === countryId)
        let index = yachtModel.countries.findIndex(c => c.id === countryId)
        country = { ...country, pivotDataDirty: pivotData }

        this.$set(yachtModel.countries, index, country)

        isDirty ? this.addToChanged(yachtModel.id, country.id) : this.removeFromChanged(yachtModel.id, country.id)
      },
      updateYachtModelCountryPivots(data) {
        this.yachtModels.loading = true

        data.map(yachtModelCountry => {
          const yachtModel = this.yachtModels.data.find(ym => ym.id === yachtModelCountry.yacht_model_id)
          const country = yachtModel.countries.find(c => c.id === yachtModelCountry.country_id)

          country.pivotData = country.pivotDataDirty = {
            currency: yachtModelCountry.currency,
            cost_cdr: yachtModelCountry.cost_cdr,
            income_safety_factor: yachtModelCountry.income_safety_factor,
            yearly_income: yachtModelCountry.yearly_income,
            projected_chartered_weeks: yachtModelCountry.projected_chartered_weeks,
            average_weekly_price: yachtModelCountry.average_weekly_price,
            yearly_expense: yachtModelCountry.yearly_expense
          }
        })

        this.yachtModels.loading = false
      },
      transformYachtModelsArray(yachtModels) {
        return yachtModels.map(ym => ({
          id: ym.id,
          full_label: ym.full_label,
          model_year: ym.model_year,
          cabin_count_description: ym.cabin_count_description,
          berth_count_description: ym.berth_count_description,
          countries: ym.countries.map(c => ({
            id: c.id,
            name: c.name,
            pivotData: {
              currency: ym.currency,
              cost_cdr: c.pivot.cost_cdr,
              income_safety_factor: c.pivot.income_safety_factor,
              yearly_income: c.pivot.yearly_income,
              projected_chartered_weeks: c.pivot.projected_chartered_weeks,
              average_weekly_price: c.pivot.average_weekly_price,
              yearly_expense: c.pivot.yearly_expense
            },
            pivotDataDirty: {
              currency: ym.currency,
              cost_cdr: c.pivot.cost_cdr,
              income_safety_factor: c.pivot.income_safety_factor,
              yearly_income: c.pivot.yearly_income,
              projected_chartered_weeks: c.pivot.projected_chartered_weeks,
              average_weekly_price: c.pivot.average_weekly_price,
              yearly_expense: c.pivot.yearly_expense
            }
          }))
        }))
      },
      addToChanged(yachtModelId, countryId) {
        let index = this.changed.findIndex(x => x.yacht_model_id === yachtModelId && x.country_id === countryId)

        if (index > 0) return

        this.changed.push({ yacht_model_id: yachtModelId, country_id: countryId })
      },
      removeFromChanged(yachtModelId, countryId) {
        let index = this.changed.findIndex(x => x.yacht_model_id === yachtModelId && x.country_id === countryId)

        if (index < 0) return

        this.changed.splice(index, 1)
      },
      discardChanges() {
        this.changed.map(item => {
          let yachtModel = this.yachtModels.data.find(ym => ym.id === item.yacht_model_id)
          let country = yachtModel.countries.find(c => c.id === item.country_id)
          let index = yachtModel.countries.findIndex(c => c.id === country.id)
          country = { ...country, pivotDataDirty: country.pivotData }

          this.$set(yachtModel.countries, index, country)
        })

        this.changed = []
      },
      async syncSheets() {
        this.sheetsSyncing = true

        try {
          await YachtModel.api.syncFromSheets()

          this.$store.dispatch('alert', {
            text: 'Yacht model incomes and costs synced from Google Sheets!',
            color: 'success'
          })

          this.fetchYachtModels()
        } catch {
          this.$store.dispatch('alert', {
            text: 'Error syncing from sheets, try again later!',
            color: 'error'
          })
        } finally {
          this.sheetsSyncing = false
        }
      }
    }
  }
</script>

<style lang="css" scoped>
  th {
    position: sticky !important;
    top: 0;
    background: white;
    z-index: 10;
  }
  td, th {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  td:last-child, th:last-child {
    border-right: none;
  }
</style>
